import React, { useState, useEffect } from 'react';
import { Container, Card, Table, Button, Modal, Form, Badge, Alert } from 'react-bootstrap';
import { FiPlus, FiEdit2, FiTrash2, FiMonitor, FiCheckCircle, FiArrowLeft } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import api from '../services/api';

const RoomManagement = () => {
    const [rooms, setRooms] = useState([]);
    const [showModal, setShowModal] = useState(false);
    const [editingRoom, setEditingRoom] = useState(null);
    const [formData, setFormData] = useState({ nome: '', descricao: '', capacidade: '' });
    const [feedback, setFeedback] = useState({ type: '', text: '' });

    useEffect(() => {
        fetchRooms();
    }, []);

    const fetchRooms = async () => {
        try {
            const response = await api.get('/rooms');
            setRooms(response.data);
        } catch (error) {
            console.error('Erro ao buscar salas', error);
            setFeedback({ type: 'danger', text: 'Erro ao carregar lista de salas.' });
        }
    };

    const handleShow = (room = null) => {
        if (room) {
            setEditingRoom(room);
            setFormData({
                nome: room.nome,
                descricao: room.descricao || '',
                capacidade: room.capacidade || ''
            });
        } else {
            setEditingRoom(null);
            setFormData({ nome: '', descricao: '', capacidade: '' });
        }
        setShowModal(true);
        setFeedback({ type: '', text: '' });
    };

    const handleClose = () => setShowModal(false);

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (editingRoom) {
                await api.put(`/rooms/${editingRoom.id}`, formData);
                setFeedback({ type: 'success', text: 'Sala atualizada com sucesso!' });
            } else {
                await api.post('/rooms', formData);
                setFeedback({ type: 'success', text: 'Sala criada com sucesso!' });
            }
            fetchRooms();
            handleClose();
        } catch (error) {
            console.error('Erro ao salvar sala', error);
            setFeedback({ type: 'danger', text: 'Erro ao salvar sala. Verifique os dados.' });
        }
    };

    const handleToggleStatus = async (room) => {
        if (!window.confirm(`Tem certeza que deseja ${room.ativa ? 'desativar' : 'ativar'} esta sala?`)) return;

        try {
            await api.put(`/rooms/${room.id}`, { ativa: !room.ativa });
            fetchRooms();
        } catch (error) {
            console.error('Erro ao alterar status', error);
            setFeedback({ type: 'danger', text: 'Erro ao alterar status da sala.' });
        }
    };

    return (
        <Container className="mt-4">
            <div className="mb-4">
                <Button 
                    variant="link" 
                    as={Link} 
                    to="/dashboard" 
                    className="text-decoration-none p-0 mb-2 text-muted"
                >
                    <FiArrowLeft className="me-1" /> Voltar para Dashboard
                </Button>                
            </div>
            <div className="d-flex justify-content-between align-items-center">
                <h2 className="mb-4 fw-bold" style={{ fontFamily: 'Nunito, sans-serif', color: '#006064' }}>
                    Gerenciamento de Salas
                </h2>
                <Button variant="success" onClick={() => handleShow()} className="fw-bold text-white">
                    <FiPlus className="me-1" /> Nova Sala
                </Button>
            </div>

            {feedback.text && (
                <Alert variant={feedback.type} onClose={() => setFeedback({ type: '', text: '' })} dismissible>
                    {feedback.text}
                </Alert>
            )}

            <Card className="shadow-sm border-0">
                <Card.Body className="p-0">
                    <Table hover responsive className="mb-0">
                        <thead className="bg-light">
                            <tr>
                                <th className="border-0 text-muted small fw-bold text-uppercase ps-4">Nome</th>
                                <th className="border-0 text-muted small fw-bold text-uppercase">Descrição</th>
                                <th className="border-0 text-muted small fw-bold text-uppercase">Capacidade</th>
                                <th className="border-0 text-muted small fw-bold text-uppercase">Status</th>
                                <th className="border-0 text-muted small fw-bold text-uppercase text-end pe-4">Ações</th>
                            </tr>
                        </thead>
                        <tbody>
                            {rooms.length === 0 ? (
                                <tr>
                                    <td colSpan="5" className="text-center py-4 text-muted">
                                        Nenhuma sala cadastrada.
                                    </td>
                                </tr>
                            ) : (
                                rooms.map(room => (
                                    <tr key={room.id} className={`align-middle ${!room.ativa ? 'bg-light text-muted' : ''}`}>
                                        <td className="ps-4 fw-bold text-dark">{room.nome}</td>
                                        <td>{room.descricao || '-'}</td>
                                        <td>{room.capacidade || '-'}</td>
                                        <td>
                                            <Badge bg={room.ativa ? 'success' : 'secondary'} className="fw-normal px-3 py-2 rounded-pill">
                                                {room.ativa ? 'Ativa' : 'Inativa'}
                                            </Badge>
                                        </td>
                                        <td className="text-end pe-4">
                                            <Button 
                                                variant="outline-primary" 
                                                size="sm" 
                                                className="me-2 d-inline-flex align-items-center gap-1" 
                                                onClick={() => handleShow(room)} 
                                                title="Editar"
                                            >
                                                <FiEdit2 size={14} /> Editar
                                            </Button>
                                            <Button 
                                                variant={room.ativa ? "outline-danger" : "outline-success"}
                                                size="sm" 
                                                className="d-inline-flex align-items-center gap-1"
                                                onClick={() => handleToggleStatus(room)} 
                                                title={room.ativa ? "Desativar" : "Ativar"}
                                            >
                                                {room.ativa ? <><FiTrash2 size={14} /> Desativar</> : <><FiCheckCircle size={14} /> Ativar</>}
                                            </Button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </Table>
                </Card.Body>
            </Card>

            <Modal show={showModal} onHide={handleClose} centered>
                <Modal.Header closeButton className="border-0 pb-0">
                    <Modal.Title className="fw-bold">{editingRoom ? 'Editar Sala' : 'Nova Sala'}</Modal.Title>
                </Modal.Header>
                <Form onSubmit={handleSubmit}>
                    <Modal.Body>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold text-muted small text-uppercase">Nome da Sala / Recurso</Form.Label>
                            <Form.Control 
                                type="text" 
                                name="nome" 
                                value={formData.nome} 
                                onChange={handleChange} 
                                required 
                                placeholder="Ex: Sala de Reunião 1, Auditório A"
                                className="border-0 bg-light"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold text-muted small text-uppercase">Descrição (Opcional)</Form.Label>
                            <Form.Control 
                                as="textarea" 
                                rows={2}
                                name="descricao" 
                                value={formData.descricao} 
                                onChange={handleChange} 
                                placeholder="Detalhes sobre equipamentos, localização, etc."
                                className="border-0 bg-light"
                            />
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label className="fw-bold text-muted small text-uppercase">Capacidade (Pessoas)</Form.Label>
                            <Form.Control 
                                type="number" 
                                name="capacidade" 
                                value={formData.capacidade} 
                                onChange={handleChange} 
                                min="1"
                                className="border-0 bg-light"
                            />
                        </Form.Group>
                    </Modal.Body>
                    <Modal.Footer className="border-0 pt-0">
                        <Button variant="link" onClick={handleClose} className="text-decoration-none text-muted">Cancelar</Button>
                        <Button variant="success" type="submit" className="fw-bold text-white px-4">Salvar</Button>
                    </Modal.Footer>
                </Form>
            </Modal>
        </Container>
    );
};

export default RoomManagement;
